import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-custom-input',
  templateUrl: './custom-input.component.html',
  styleUrls: ['./custom-input.component.scss'],
  exportAs: 'appCustomInput',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomInputComponent),
      multi: true,
    },
  ],
})
export class CustomInputComponent implements OnChanges, ControlValueAccessor {
  /** The current string/number value. Supports two-way binding: [(value)]="myVar" */
  @Input() value: string | number | null = null;

  /** HTML input type — 'text' | 'number' | 'time' | 'date' | 'email' | etc. */
  @Input() type = 'text';

  /** Placeholder text shown inside the input when empty. */
  @Input() placeholder = '';

  /** Font Awesome class for the leading (left) icon, e.g. 'fa fa-clock'. Set to null to hide. */
  @Input() icon: string | null = null;

  /** Font Awesome class for the trailing (right) icon, e.g. 'fa fa-search'. Set to null to hide. */
  @Input() trailingIcon: string | null = null;

  /** When true, all interaction is blocked. Works with [disabled] binding and reactive-forms setDisabledState. */
  @Input()
  set disabled(val: boolean) {
    this.isDisabled = !!val;
  }
  get disabled(): boolean { return this.isDisabled; }

  /** When true, the input is read-only (focusable but not editable). */
  @Input() readonly = false;

  /** When true, removes the border and background ("ghost" appearance). */
  @Input() naked = false;

  /** Max character / numeric length passed directly to the <input> element. */
  @Input() maxlength: number | null = null;

  /** Min value for numeric/date/time inputs. */
  @Input() min: string | number | null = null;

  /** Max value for numeric/date/time inputs. */
  @Input() max: string | number | null = null;

  /** Emits the new value on every keystroke — enables [(value)] two-way binding. */
  @Output() valueChange = new EventEmitter<string | number | null>();

  /** Emits when the native input emits a `change` event (on blur/commit). */
  @Output() inputChange = new EventEmitter<string | number | null>();

  /** Emits when the input receives focus. */
  @Output() inputFocus = new EventEmitter<FocusEvent>();

  /** Emits when the input loses focus. */
  @Output() inputBlur = new EventEmitter<FocusEvent>();

  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  /** Direct reference to the native <input> element. */
  get nativeInput(): HTMLInputElement | null {
    return this.inputEl?.nativeElement ?? null;
  }

  isFocused = false;
  isDisabled = false;

  @HostBinding('class.ci-disabled') get hostDisabledClass() { return this.isDisabled; }
  @HostBinding('attr.aria-disabled') get hostAriaDisabled() { return this.isDisabled || null; }

  // ── ControlValueAccessor callbacks ────────────────────────────────────────
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    // no derived state needed; inputs are used directly in template
  }

  // ── ControlValueAccessor implementation ───────────────────────────────────

  writeValue(value: any): void {
    this.value = value !== undefined ? value : null;
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // ── Interaction ───────────────────────────────────────────────────────────

  onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const emitted = this.type === 'number' && raw !== '' ? +raw : raw || null;
    this.value = emitted;
    this._onChange(emitted);
    this.valueChange.emit(emitted);
  }

  onChange(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const emitted = this.type === 'number' && raw !== '' ? +raw : raw || null;
    this.inputChange.emit(emitted);
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.inputFocus.emit(event);
  }

  onBlur(event: FocusEvent): void {
    this.isFocused = false;
    this._onTouched();
    this.inputBlur.emit(event);
  }

  /** Programmatically focus the underlying native input. */
  focus(): void {
    this.inputEl?.nativeElement.focus();
  }
}
